import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useSystemMembers, SystemMember } from '@/hooks/useSystemMembers';
import { MemberCard } from '@/components/admin/MemberCard';
import { FilterControls } from '@/components/admin/FilterControls';
import { ResponsivePagination } from '@/components/admin/ResponsivePagination';
import { 
  Users, 
  Shield, 
  Crown, 
  User, 
  Building2, 
  Activity, 
  Calendar, 
  MessageSquare,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

const getRoleBadgeConfig = (role: SystemMember['platform_role']) => {
  switch (role) {
    case 'super_admin':
      return {
        label: 'Super Admin',
        icon: Crown,
        className: 'bg-gradient-to-r from-purple-600 to-purple-700 text-white border-purple-300',
      };
    case 'admin':
      return {
        label: 'Admin',
        icon: Shield,
        className: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-300',
      };
    case 'member':
      return {
        label: 'Member',
        icon: User,
        className: 'bg-gradient-to-r from-green-600 to-green-700 text-white border-green-300',
      };
    case 'guest':
    default:
      return {
        label: 'Guest',
        icon: User,
        className: 'bg-gradient-to-r from-gray-600 to-gray-700 text-white border-gray-300',
      };
  }
};

const getEngagementConfig = (level: 'low' | 'medium' | 'high') => {
  switch (level) {
    case 'high':
      return { icon: TrendingUp, className: 'text-green-600', label: 'High' };
    case 'medium':
      return { icon: Minus, className: 'text-yellow-600', label: 'Medium' };
    case 'low':
    default:
      return { icon: TrendingDown, className: 'text-red-600', label: 'Low' };
  }
};

export const SystemMembersPanel = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [engagementFilter, setEngagementFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, error } = useSystemMembers({ page: currentPage, pageSize });
  const members = data?.members || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 1;

  // Apply client-side filtering for search and filters
  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      !searchQuery || 
      member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.phone_number?.includes(searchQuery);
    
    const matchesRole = roleFilter === 'all' || member.platform_role === roleFilter;
    const matchesEngagement = engagementFilter === 'all' || member.insights.engagement_level === engagementFilter;
    
    return matchesSearch && matchesRole && matchesEngagement;
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            System Members
          </CardTitle>
          <CardDescription>Loading member data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="md:flex md:items-center md:space-x-4 space-y-2 md:space-y-0">
                <Skeleton className="h-12 w-12 rounded-full mx-auto md:mx-0" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-[250px] mx-auto md:mx-0" />
                  <Skeleton className="h-4 w-[200px] mx-auto md:mx-0" />
                </div>
                <Skeleton className="h-8 w-[100px] mx-auto md:mx-0" />
                <Skeleton className="h-8 w-[80px] mx-auto md:mx-0" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Error Loading Members
          </CardTitle>
          <CardDescription>Failed to load system members data.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Note: For now showing current page stats. In production, these should be separate queries for system-wide stats
  const stats = {
    total: totalCount,
    superAdmins: members.filter(m => m.platform_role === 'super_admin').length,
    admins: members.filter(m => m.platform_role === 'admin').length,
    activeUsers: members.filter(m => m.insights.days_since_last_activity !== null && m.insights.days_since_last_activity < 30).length,
    multiOrgUsers: members.filter(m => m.insights.is_multi_org).length,
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Members</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.total}</p>
              </div>
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Super Admins</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.superAdmins}</p>
              </div>
              <Crown className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Admins</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.admins}</p>
              </div>
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active (30d)</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.activeUsers}</p>
              </div>
              <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Multi-Org</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.multiOrgUsers}</p>
              </div>
              <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Members Panel */}
      <Card>
        <CardHeader>
          <div className="space-y-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                All System Members
              </CardTitle>
              <CardDescription>
                Comprehensive view of all users across organizations with activity insights
              </CardDescription>
            </div>
            
            {/* Filter Controls */}
            <FilterControls
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              roleFilter={roleFilter}
              setRoleFilter={setRoleFilter}
              engagementFilter={engagementFilter}
              setEngagementFilter={setEngagementFilter}
              totalResults={filteredMembers.length}
            />
          </div>
        </CardHeader>
        
        <CardContent>
          {filteredMembers.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No members found matching your criteria</p>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="md:hidden space-y-4">
                {filteredMembers.map((member) => (
                  <MemberCard key={member.id} member={member} />
                ))}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Platform Role</TableHead>
                      <TableHead>Organizations</TableHead>
                      <TableHead>Activity</TableHead>
                      <TableHead>Engagement</TableHead>
                      <TableHead>Stats</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.map((member) => {
                      const roleConfig = getRoleBadgeConfig(member.platform_role);
                      const engagementConfig = getEngagementConfig(member.insights.engagement_level);
                      const RoleIcon = roleConfig.icon;
                      const EngagementIcon = engagementConfig.icon;
                      
                      return (
                        <TableRow key={member.id}>
                          <TableCell>
                            <div className="flex items-center space-x-3">
                              <Avatar>
                                <AvatarImage src={member.avatar_url || ''} />
                                <AvatarFallback>
                                  {member.name?.split(' ').map(n => n[0]).join('') || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{member.name || 'Unknown'}</p>
                                <p className="text-sm text-muted-foreground">
                                  {member.email || member.phone_number || 'No contact info'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Joined {format(new Date(member.created_at), 'MMM d, yyyy')}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <Badge className={cn("flex items-center gap-1 w-fit", roleConfig.className)}>
                              <RoleIcon className="h-3 w-3" />
                              {roleConfig.label}
                            </Badge>
                          </TableCell>
                          
                          <TableCell>
                            <div className="space-y-1">
                              {member.organization_memberships.length === 0 ? (
                                <span className="text-sm text-muted-foreground">No organizations</span>
                              ) : (
                                member.organization_memberships.slice(0, 2).map((org, index) => (
                                  <div key={org.org_id} className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">
                                      {org.org_name}
                                    </Badge>
                                    <Badge variant="secondary" className="text-xs">
                                      {org.role}
                                    </Badge>
                                    {member.insights.is_multi_org && index === 0 && (
                                      <div title="Multi-organization user">
                                        <Building2 className="h-3 w-3 text-orange-600" />
                                      </div>
                                    )}
                                  </div>
                                ))
                              )}
                              {member.organization_memberships.length > 2 && (
                                <span className="text-xs text-muted-foreground">
                                  +{member.organization_memberships.length - 2} more
                                </span>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-sm">
                                <Calendar className="h-3 w-3" />
                                {member.stats.last_activity_date 
                                  ? format(new Date(member.stats.last_activity_date), 'MMM d')
                                  : 'Never'
                                }
                              </div>
                              {member.insights.days_since_last_activity !== null && (
                                <p className="text-xs text-muted-foreground">
                                  {member.insights.days_since_last_activity}d ago
                                </p>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <EngagementIcon className={cn("h-4 w-4", engagementConfig.className)} />
                              <span className="text-sm font-medium">{engagementConfig.label}</span>
                              <Badge variant="outline" className="text-xs">
                                {member.insights.activity_score}
                              </Badge>
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-sm">
                                <span className="font-medium">{member.stats.total_bookings}</span>
                                <span className="text-muted-foreground text-xs">bookings</span>
                              </div>
                              {member.stats.support_tickets_count > 0 && (
                                <div className="flex items-center gap-1 text-xs">
                                  <MessageSquare className="h-3 w-3" />
                                  {member.stats.support_tickets_count}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {member.insights.onboarding_complete ? (
                                <div title="Onboarding complete">
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                </div>
                              ) : (
                                <div title="Incomplete onboarding">
                                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                </div>
                              )}
                              
                              {member.insights.has_admin_roles && (
                                <div title="Has admin roles">
                                  <Shield className="h-4 w-4 text-blue-600" />
                                </div>
                              )}
                              
                              {member.insights.days_since_last_activity !== null && 
                               member.insights.days_since_last_activity > 90 && (
                                <div title="Inactive user">
                                  <AlertTriangle className="h-4 w-4 text-red-600" />
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="mt-6">
                <ResponsivePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};